import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const MAX_LEN = 2000;

/**
 * Free-form text override for a single meal.
 *
 * When the user saves non-empty text, the meal card switches to render that
 * text verbatim (instead of the structured meal + macros UI). Useful when
 * what they actually ate doesn't fit the AI's structured plan — e.g.
 * "Leftover pasta + protein shake (~520 cal, 32g P)".
 *
 * Clearing the text (empty save) restores the AI-generated structured view.
 */
export const MealRawTextEditorDialog = ({ open, onClose, api, planId, weekIndex, mealIndex, initialText, mealLabel, onSaved }) => {
  const [text, setText] = useState(initialText || '');
  const [saving, setSaving] = useState(false);

  // Re-seed the textarea every time the dialog opens for a different meal.
  useEffect(() => {
    if (open) setText(initialText || '');
  }, [open, initialText]);

  const save = async (clearOverride = false) => {
    setSaving(true);
    try {
      const res = await api().patch(`/nutrition-plans/${planId}/meal-raw-text`, {
        week_index: weekIndex,
        meal_index: mealIndex,
        raw_text: clearOverride ? null : text,
      });
      toast.success(clearOverride ? 'Reverted to AI version' : 'Meal text saved');
      onSaved?.(res.data.plan);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="meal-raw-text-overlay"
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95 }}
          className="bg-card border border-border rounded-2xl max-w-lg w-full"
          onClick={(e) => e.stopPropagation()}
          data-testid="meal-raw-text-dialog"
        >
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="min-w-0">
              <h3 className="font-heading text-base text-white uppercase tracking-wider truncate">
                Edit Meal{mealLabel ? ` — ${mealLabel}` : ''}
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Pre-filled with the current suggestion — tweak the foods or macros, or clear and start over.
              </p>
            </div>
            <button
              onClick={onClose}
              className="shrink-0 h-8 w-8 rounded-md bg-white/5 hover:bg-white/10 text-muted-foreground flex items-center justify-center"
              aria-label="Close"
              data-testid="meal-raw-text-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="p-4 space-y-3">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
              placeholder={'e.g. "1 cup oats with banana and almond butter (~350 cal, 12g P, 50g C, 10g F)"'}
              rows={6}
              className="bg-black border-border text-white resize-none"
              data-testid="meal-raw-text-input"
            />
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>Markdown supported. Includes whatever notes / macros you want.</span>
              <span>{text.length}/{MAX_LEN}</span>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 p-4 border-t border-border">
            {initialText ? (
              <Button
                variant="outline"
                onClick={() => save(true)}
                disabled={saving}
                className="text-red-400 border-red-500/30 hover:bg-red-500/10 hover:text-red-300"
                data-testid="meal-raw-text-revert"
              >
                Revert to AI Version
              </Button>
            ) : <span />}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={onClose} disabled={saving} className="border-border">
                Cancel
              </Button>
              <Button
                onClick={() => save(false)}
                disabled={saving || !text.trim()}
                className="bg-primary text-black hover:bg-primary/90"
                data-testid="meal-raw-text-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
